/**
 * Tier policy — the single source of truth for what each subscription tier
 * is allowed to do.
 *
 * ## Why this file exists
 *
 * Before this module the codebase had ~50 separate sites doing
 *   `tier = workspace.subscriptionTier → look up TIER_DEFINITIONS → run check`
 * with slight variations. Adding a new quota or tier meant editing every
 * one of them. This file fixes that:
 *
 * 1. **Pure data**, no behaviour. Adding a new metered AI feature is one
 *    new literal in `AiQuotaFeature` and one `q(...)` entry per tier.
 *    Adding a new tier is one new top-level entry; every gate, marketing
 *    surface, and snapshot mechanically picks it up.
 * 2. **Resolver lives in the backend** (`workspace-policy.service.ts`).
 *    Business logic NEVER imports `TIER_POLICY` directly — it goes
 *    through `policyService.canX()` / `.resolve()`.
 * 3. **Stripe is the source of truth for billing state**. The price IDs
 *    here are environment-variable handles, not hardcoded values; the
 *    backend resolves them via Secret Manager (`getConfig`).
 *
 * The legacy `tiers.ts` API (`TIER_DEFINITIONS`, the `canX` helpers) is
 * a thin re-export built on top of this module so existing imports keep
 * compiling during the refactor. New code should import from
 * `@bower/shared` and pick `TIER_POLICY` directly.
 */
export const AI_QUOTA_FEATURES = [
    'chat',
    'liveMinutes',
    'textExtractions',
    'transcriptionMinutes',
    'codeExecutionMinutes',
];
const TIER_FEATURE_KEYS = [
    'phi',
    'publicSharing',
    'prioritySupport',
    'inferredRelationships',
    'personalMemory',
    'workspaceInstructions',
    'knowledgeGraph',
    'trashRestore',
    'privacyControls',
    'workspaceRoles',
    'complianceLockdown',
];
/** @internal Iteration order for any UI that lists features; read by policy.test.ts. */
export function tierFeatureKeys() {
    return TIER_FEATURE_KEYS;
}
// ---------------------------------------------------------------------------
// Quota factory — keeps the TIER_POLICY table readable.
// ---------------------------------------------------------------------------
function q(limit, perSeat, unit, nounSingular, nounPlural) {
    return { limit, perSeat, unit, nounSingular, nounPlural };
}
const GB = 1024 * 1024 * 1024;
/**
 * The only point at which an audit row is ever destroyed, for every tier.
 * `retention.auditDays` is now a SEARCH WINDOW, not a delete schedule: rows
 * outside a workspace's tier window still exist and reappear on upgrade.
 * See docs/planning/audit-log-chain-of-custody.md, decision D3.
 */
export const AUDIT_ABSOLUTE_RETENTION_DAYS = 7 * 365;
// ---------------------------------------------------------------------------
// TIER_POLICY — THE source of truth. Adding a tier here is sufficient.
// ---------------------------------------------------------------------------
export const TIER_POLICY = {
    FREE: {
        tier: 'FREE',
        displayName: 'Starter',
        billing: { stripePrice: null, maxSeats: 1, minSeats: 1 },
        // Unlimited guests on every tier (2026-09 pricing change). A guest is a
        // VIEWER: read-only, no paid seat. Capping them was throttling the thing
        // that spreads a workspace through a lab, and the paid gate is the second
        // MEMBER, not the reader. Both fields null is the documented 'unlimited'
        // form (see GuestSpec) — the backend gate skips on a null cap and both
        // display paths already render it as unlimited.
        guests: { maxTotal: null, perPaidSeat: null },
        // Raised 1 GB → 50 GB (2026-08-17). 1 GB was the binding constraint on
        // real FREE workspaces — the only quota that produced hard 402s in prod
        // — and it bit exactly the early users whose feedback we want. Storage
        // is our cheapest COGS line (GCS at rest), so headroom here buys
        // retention far more cheaply than it costs.
        storage: { bytesPerSeat: 50 * GB, pooled: false },
        aiQuotas: {
            // Recalibrated 2026-06-23 from real free-tier usage (chat ~P90) + COGS
            // backstops for the minute dimensions (no real free-tier live/voice
            // signal — internal heavy users are quotaExempt). See
            // docs/planning/2026-06-23-ai-quota-enforcement-and-escape-hatch.md.
            chat: q(100, false, 'count', 'chat', 'chats'),
            liveMinutes: q(10, false, 'minutes', 'minute', 'minutes'),
            textExtractions: q(30, false, 'count', 'extraction', 'extractions'),
            transcriptionMinutes: q(30, false, 'minutes', 'minute', 'minutes'),
            // Code-execution sandbox is a billable Vertex compute SKU. FREE has a
            // real, measured allowance rather than zero: 10 mirrors `liveMinutes`
            // (the closest analogue, since both meter compute time). Whether this
            // cap actually blocks anyone is controlled centrally, NOT by the
            // number here — see `AI_QUOTA_ENFORCE_DEFAULT` in
            // `config/secrets.yaml` and `agent_service/services/quota_gate.py`.
            // With that switch off (production, as of this writing), usage
            // debits in shadow mode and nothing is refused. A previous version of
            // this comment claimed a cap of 0 was "the hard backstop so a FREE
            // workspace can never spin a paid sandbox" — that was only ever true
            // because the agent's sandbox gate independently mis-derived
            // enforcement from cap/remaining alone, ignoring the switch above. See
            // the choke-point module for why that can't drift again.
            codeExecutionMinutes: q(10, false, 'minutes', 'minute', 'minutes'),
        },
        features: {
            phi: false,
            publicSharing: false,
            prioritySupport: false,
            inferredRelationships: false,
            // Personal + workspace agent memory are available on every tier
            // (incl. FREE) — they add baseline quality, not a paid differentiator.
            personalMemory: true,
            workspaceInstructions: true,
            // Available on EVERY tier (2026-07-27). The visualiser only renders
            // relationships the workspace already has — linked entities, protocol
            // and collection membership — so it costs us nothing per view and it's
            // the clearest demonstration of why capturing into Bower compounds.
            // The paid differentiator stays `inferredRelationships`: FREE sees the
            // edges it created, PRO+ gets the edges Bird discovers.
            knowledgeGraph: true,
            // Available on EVERY tier (2026-10-01). The "Moved to trash" toast's
            // Undo restores through the same gated endpoint, so on Starter the app
            // offered Undo and then refused it with an upgrade prompt. Getting
            // back something you deleted a moment ago is not a premium feature,
            // and soft-delete costs us nothing extra to keep restorable for the
            // window the purge job already enforces.
            trashRestore: true,
            // Available on EVERY tier (2026-07-26). Deciding who can see a note is
            // a safety property, not a premium feature — a solo researcher on FREE
            // handling sensitive work needs it as much as a Team does, and gating
            // it taught people that Bower's default is "everyone in the workspace
            // sees everything". The HIPAA/BAA posture (`phi`) stays TEAM-only;
            // that's the part that actually costs us something.
            privacyControls: true,
            workspaceRoles: false,
            // Follows `privacyControls`, because it has to. This flag names "Just
            // me" mode, but the only code that gates "Just me" is
            // `getPrivateModeAllowance`, which reads `privacyControls` alone. Left
            // false here, FREE would get the capability while the pricing compare
            // matrix printed a ✗ against it — the plan page contradicting the
            // product.
            complianceLockdown: true,
        },
        retention: { auditDays: 30, snapshotDays: 7 },
        connectors: { maxPerUser: 3 },
    },
    PRO: {
        tier: 'PRO',
        displayName: 'Individual',
        billing: {
            stripePrice: { monthly: 'STRIPE_PRO_MONTHLY_PRICE_ID', annual: 'STRIPE_PRO_ANNUAL_PRICE_ID' },
            maxSeats: 1,
            minSeats: 1,
        },
        // Unlimited guests on every tier (2026-09 pricing change). A guest is a
        // VIEWER: read-only, no paid seat. Capping them was throttling the thing
        // that spreads a workspace through a lab, and the paid gate is the second
        // MEMBER, not the reader. Both fields null is the documented 'unlimited'
        // form (see GuestSpec) — the backend gate skips on a null cap and both
        // display paths already render it as unlimited.
        guests: { maxTotal: null, perPaidSeat: null },
        storage: { bytesPerSeat: 100 * GB, pooled: false },
        aiQuotas: {
            chat: q(500, false, 'count', 'chat', 'chats'),
            liveMinutes: q(30, false, 'minutes', 'minute', 'minutes'),
            textExtractions: q(50, false, 'count', 'extraction', 'extractions'),
            transcriptionMinutes: q(50, false, 'minutes', 'minute', 'minutes'),
            codeExecutionMinutes: q(30, false, 'minutes', 'minute', 'minutes'),
        },
        features: {
            // Restricted mode (HIPAA-ready) is TEAM-only — requires Bower's BAA
            // posture which we only offer on Team contracts.
            phi: false,
            publicSharing: true,
            prioritySupport: false,
            inferredRelationships: true,
            personalMemory: true,
            workspaceInstructions: true,
            knowledgeGraph: true,
            trashRestore: true,
            // Available on every tier — see the FREE entry.
            privacyControls: true,
            workspaceRoles: false,
            // "Just me" compliance lockdown is available on Pro + Team —
            // researchers on a single-seat Pro plan still want the option
            // to lock a workspace to themselves for regulated work.
            complianceLockdown: true,
        },
        retention: { auditDays: 365, snapshotDays: 365 },
        connectors: { maxPerUser: 10 },
    },
    TEAM: {
        tier: 'TEAM',
        displayName: 'Team',
        billing: {
            stripePrice: {
                monthly: 'STRIPE_TEAM_MONTHLY_PRICE_ID',
                annual: 'STRIPE_TEAM_ANNUAL_PRICE_ID',
            },
            maxSeats: null,
            minSeats: 1,
        },
        // Unlimited guests on every tier (2026-09 pricing change). A guest is a
        // VIEWER: read-only, no paid seat. Capping them was throttling the thing
        // that spreads a workspace through a lab, and the paid gate is the second
        // MEMBER, not the reader. Both fields null is the documented 'unlimited'
        // form (see GuestSpec) — the backend gate skips on a null cap and both
        // display paths already render it as unlimited.
        guests: { maxTotal: null, perPaidSeat: null },
        storage: { bytesPerSeat: 500 * GB, pooled: true },
        aiQuotas: {
            chat: q(1000, true, 'count', 'chat', 'chats'),
            liveMinutes: q(60, true, 'minutes', 'minute', 'minutes'),
            textExtractions: q(100, true, 'count', 'extraction', 'extractions'),
            transcriptionMinutes: q(100, true, 'minutes', 'minute', 'minutes'),
            codeExecutionMinutes: q(60, true, 'minutes', 'minute', 'minutes'),
        },
        features: {
            phi: true,
            publicSharing: true,
            prioritySupport: true,
            inferredRelationships: true,
            personalMemory: true,
            workspaceInstructions: true,
            knowledgeGraph: true,
            trashRestore: true,
            privacyControls: true,
            workspaceRoles: true,
            complianceLockdown: true,
        },
        retention: { auditDays: 7 * 365, snapshotDays: 7 * 365 },
        connectors: { maxPerUser: null },
    },
};
export const TIER_HIERARCHY = ['FREE', 'PRO', 'TEAM'];
/**
 * Paid seats a card-free trial may reach before a payment method is
 * required.
 *
 * Bounds trial COGS: every TEAM AI quota is `perSeat: true` and storage is
 * per-seat pooled, so seats are the cost multiplier. Adds beyond this cap
 * keep the pre-2026-08 fail-closed behaviour (PAYMENT_METHOD_REQUIRED
 * unless a promotion makes the increment genuinely zero-cost).
 *
 * Only TEAM can reach this: FREE and PRO are single-seat
 * (`billing.maxSeats: 1`) and are refused at `canPurchaseSeat` first.
 */
export const TRIAL_CARDLESS_SEAT_CAP = 10;
// ---------------------------------------------------------------------------
// Pure derived helpers. The backend's policy resolver layers
// per-workspace state on top of these; everything below is data-only.
// ---------------------------------------------------------------------------
export function tierPolicy(tier) {
    return TIER_POLICY[tier];
}
/**
 * The workspace's effective storage cap in bytes given its current paid
 * seat count. On per-member tiers (TEAM today): `bytesPerSeat ×
 * paidSeatCount`, pooled. Otherwise: the flat base.
 *
 * Floors at 1 paid seat so a workspace in transition (no seats yet)
 * still gets one slice rather than zero.
 */
export function effectiveStorageBytes(tier, paidSeatCount) {
    const spec = TIER_POLICY[tier].storage;
    if (!spec.pooled)
        return spec.bytesPerSeat;
    return spec.bytesPerSeat * Math.max(1, paidSeatCount);
}
/**
 * The workspace's effective AI cap for a feature given its paid-seat
 * count. On `perSeat: true` quotas: `limit × paidSeatCount`. Otherwise:
 * the flat limit. null = unlimited.
 *
 * Returned value is the snapshot to write into
 * `workspace_ai_quota_counters.cap_snapshot` at period start.
 */
export function effectiveAiQuota(tier, feature, paidSeatCount) {
    const spec = TIER_POLICY[tier].aiQuotas[feature];
    if (spec.limit === null)
        return null;
    if (!spec.perSeat)
        return spec.limit;
    return spec.limit * Math.max(1, paidSeatCount);
}
/**
 * The workspace's effective guest cap given its paid seat count. Null
 * = unlimited guests. Zero means the tier has no guest allowance and
 * the policy resolver should refuse any guest invite.
 */
export function effectiveMaxGuests(tier, paidSeatCount) {
    const spec = TIER_POLICY[tier].guests;
    if (spec.maxTotal !== null)
        return spec.maxTotal;
    if (spec.perPaidSeat === null)
        return null; // unlimited (no tier uses this today)
    return spec.perPaidSeat * Math.max(0, paidSeatCount);
}
/** Whether `featureKey` is enabled on the tier. */
export function hasTierFeature(tier, featureKey) {
    return TIER_POLICY[tier].features[featureKey];
}
/**
 * Lowest tier in the ladder that turns `featureKey` on. Returns null when
 * no tier provides the feature. Used by every gate site (route middleware,
 * service guards, frontend `useCanUseFeature`) so the upgrade prompt
 * points at the correct plan. Centralised here so changing the gate's
 * minimum tier in TIER_POLICY automatically flows through every caller.
 */
export function minimumTierForFeature(featureKey) {
    return TIER_HIERARCHY.find((t) => TIER_POLICY[t].features[featureKey]) ?? null;
}
/** @internal Whether `current` is at or above `minimum` in the tier ladder; read by policy.test.ts. */
export function tierAtLeast(current, minimum) {
    return TIER_HIERARCHY.indexOf(current) >= TIER_HIERARCHY.indexOf(minimum);
}
//# sourceMappingURL=policy.js.map